import React, { useCallback, useMemo } from "react";
import { Grid } from "@mui/material";
import { colors } from "@zapalacorp/tokens";
import { useTranslation } from "react-i18next";
import { FiPlus } from "react-icons/fi";

import { Button, Input } from "Components/Form";
import IconCloseCircle from "Components/Generic/Icons/CloseCircle";
import {
  ErrorMessage,
  ErrorWrapper,
  IconButtonClose,
  SlotTimeFooter,
  SlotTimeForm,
  SlotTimeHeader,
  SlotTimeHeaderLabel,
  SlotTimeWrapper,
} from "./styles";
import { Task } from "Models/task";

import * as yup from "yup";
import useYupValidation from "Hooks/useYupValidation";
import { AiOutlineInfoCircle } from "react-icons/ai";
import TaskDateUtils from "Util/dateUtils";
import { useAppState } from "Context/StateContext";

interface VariableSlotTimeInterface {
  task?: Task;
  setTask: (task?: Partial<Task>) => void;
  errors?: yup.ValidationError[];
  currentDate?: Date;
  currentTimezone?: string;
}

export function VariableSlotTime({
  task,
  setTask,
  errors,
  currentDate,
  currentTimezone,
}: VariableSlotTimeInterface) {
  const { formatWithUserTimezone, userTimezone, defaultTimezone } = useAppState();
  const { t: translate } = useTranslation();
  const { getErrorByField, fieldHasError } = useYupValidation(errors || []);

  const timezone = currentTimezone || userTimezone?.name || defaultTimezone;
  const managerTaskProps = currentTimezone
    ? { timezone }
    : { formatFunction: formatWithUserTimezone };

  const timeSlots = useMemo(() => {
    return (
      task?.timeSlots || [
        {
          date: TaskDateUtils.createTaskDateTime(
            {
              dateInput: TaskDateUtils.getTodayForInput(currentDate, managerTaskProps),
              timeInput: "09:00",
            },
            timezone
          ),
        },
      ]
    );
  }, [task, currentDate]);

  const slotTimeDate = useCallback((date?: string) => {
    if (!date) return "";

    return TaskDateUtils.extractDateForInput(date, managerTaskProps);
  }, []);

  const hasTimeSlots =
    timeSlots?.length && !Object.values(timeSlots?.[timeSlots?.length - 1])?.length;

  const onUpdateField = (field: string, value: string, index: number) => {
    if (index === -1) return;

    const currentArr = [...timeSlots];
    const currentSlot = { ...currentArr[index] };

    if (field === "date") {
      // Preserve existing time when changing date
      const existingFrom = currentSlot.from;
      const existingTo = currentSlot.to;

      currentSlot.date = TaskDateUtils.createTaskDateTime(
        { dateInput: value, timeInput: existingFrom || "09:00" },
        timezone
      );

      // Update from and to times to maintain the time values
      if (existingFrom) {
        currentSlot.from = existingFrom;
      } else {
        currentSlot.from = "09:00";
      }
      if (existingTo) {
        currentSlot.to = existingTo;
      } else {
        currentSlot.to = "10:00";
      }
    } else if (field === "from" || field === "to") {
      // Update time fields
      (currentSlot as any)[field] = value;

      // Update the date field to reflect the new time if we have a date
      if (currentSlot.date) {
        const dateInput = TaskDateUtils.extractDateForInput(
          currentSlot.date as string,
          managerTaskProps
        );
        if (field === "from") {
          currentSlot.date = TaskDateUtils.createTaskDateTime(
            { dateInput, timeInput: value },
            timezone
          );
        }
      }
    }

    currentArr[index] = currentSlot;
    setTask({ ...(task || {}), timeSlots: currentArr });
  };

  const onAddNewSlotTime = () => {
    const currentSlotTime = timeSlots;
    if (currentSlotTime.length > 0) {
      const lastSlotAdded = currentSlotTime[currentSlotTime?.length - 1];
      if (lastSlotAdded.date) {
        // Get the date part and add one day
        const lastDateInput = TaskDateUtils.extractDateForInput(
          lastSlotAdded.date as string,
          managerTaskProps
        );
        const nextDay = new Date(lastDateInput);
        nextDay.setDate(nextDay.getDate() + 1);
        const nextDateInput = TaskDateUtils.extractDateForInput(
          nextDay.toISOString(),
          managerTaskProps
        );

        // Create new slot with default time
        const newSlot = {
          date: TaskDateUtils.createTaskDateTime(
            { dateInput: nextDateInput, timeInput: "09:00" },
            timezone
          ),
          from: "09:00",
          to: "10:00",
        };

        setTask({ ...(task || {}), timeSlots: [...(task?.timeSlots || []), newSlot] });
      }
    }
  };

  const onRemoveLine = (index: number) => {
    if (index === -1) return;

    if (timeSlots?.length === 1) {
      const currentArr = [...timeSlots];
      currentArr[index] = {
        date: TaskDateUtils.createTaskDateTime(
          {
            dateInput: TaskDateUtils.getTodayForInput(currentDate, managerTaskProps),
            timeInput: "09:00",
          },
          timezone
        ),
        from: "09:00",
        to: "10:00",
      };
      setTask({ ...(task || {}), timeSlots: currentArr });
      return;
    }

    const currentArr = [...timeSlots];
    currentArr.splice(index, 1);
    setTask({ ...(task || {}), timeSlots: currentArr });
  };

  return (
    <>
      <SlotTimeWrapper hasError={!!fieldHasError("timeSlots")}>
        <SlotTimeHeader hasError={!!fieldHasError("timeSlots")}>
          <Grid container direction="row" spacing={2}>
            <Grid item xs={4}>
              <SlotTimeHeaderLabel>{translate("date")}</SlotTimeHeaderLabel>
            </Grid>
            <Grid item xs={4}>
              <SlotTimeHeaderLabel>{translate("from")}</SlotTimeHeaderLabel>
            </Grid>
            <Grid item xs={4}>
              <SlotTimeHeaderLabel>{translate("to")}</SlotTimeHeaderLabel>
            </Grid>
          </Grid>
        </SlotTimeHeader>
        {timeSlots?.map((slotTime, index) => (
          <SlotTimeForm key={index}>
            <Grid container direction="row" spacing={2}>
              <Grid item xs={4}>
                <Input
                  type="date"
                  min={
                    timeSlots[index - 1]?.date
                      ? (() => {
                          const prevDateInput = TaskDateUtils.extractDateForInput(
                            timeSlots[index - 1]?.date as string,
                            managerTaskProps
                          );
                          const nextDay = new Date(prevDateInput);
                          nextDay.setDate(nextDay.getDate() + 1);
                          return TaskDateUtils.extractDateForInput(
                            nextDay.toISOString(),
                            managerTaskProps
                          );
                        })()
                      : TaskDateUtils.getMinimumDate(19, managerTaskProps)
                  }
                  value={slotTimeDate(slotTime?.date as string)}
                  onChange={(event) => onUpdateField("date", event.target.value, index)}
                />
              </Grid>
              <Grid item xs={4}>
                <Input
                  type="time"
                  value={slotTime?.from || ""}
                  onChange={(event) => onUpdateField("from", event.target.value, index)}
                  error={fieldHasError(`timeSlots[${index}].from`)}
                  errorMessage={getErrorByField(`timeSlots[${index}].from`)}
                />
              </Grid>
              <Grid item xs={3}>
                <Input
                  type="time"
                  value={slotTime?.to || ""}
                  onChange={(event) => onUpdateField("to", event.target.value, index)}
                  error={fieldHasError(`timeSlots[${index}].to`)}
                  errorMessage={getErrorByField(`timeSlots[${index}].to`)}
                />
              </Grid>
              <Grid item xs={1}>
                <IconButtonClose onClick={() => onRemoveLine(index)}>
                  <IconCloseCircle width={20} height={20} color={colors.error500} />
                </IconButtonClose>
              </Grid>
            </Grid>
          </SlotTimeForm>
        ))}
        <SlotTimeFooter hasError={!!fieldHasError("timeSlots")}>
          <Button
            startIcon={<FiPlus />}
            disabled={!!hasTimeSlots}
            onClick={() => onAddNewSlotTime()}
          >
            {translate("addAnotherDate")}
          </Button>
          {fieldHasError("timeSlots") && (
            <ErrorWrapper>
              <AiOutlineInfoCircle size={20} color={colors.error500} />
              <ErrorMessage>{getErrorByField("timeSlots")}</ErrorMessage>
            </ErrorWrapper>
          )}
        </SlotTimeFooter>
      </SlotTimeWrapper>
    </>
  );
}
